import { ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { CloseButton } from '../../../CloseButton'
import { useTw } from '../../../../../utils/Styles'
import { useNavigate, Link } from 'react-router-dom'
import { Avatars } from '../../../../../utils/Avatars'
import { getHighestStatus, useWidgetOpen } from '../../../../../utils'
import { useProfile } from '../../../../../utils/Profile'
import { ChevronLeftIcon } from '@heroicons/react/outline'
import { StatusPageEventHistoryItemComponentStatus } from '../../../../../api/types'
import { useStatusPageQuery } from './StatusPage.generated'
import { getSchedule } from '../../../../../utils/WorkWeekSchedule'

export function ConversationWrapper({ children }: { children: ReactNode }) {
  const tw = useTw()
  const profile = useProfile()
  const { data } = useStatusPageQuery()
  const navigate = useNavigate()
  const [t] = useTranslation('common')
  const { responsive, isMobile, setOpen } = useWidgetOpen()
  const schedule = getSchedule(profile.inbox.workWeek)

  const issues = useMemo(
    () =>
      data?.Profile?.statusPage?.components
        .map((_) => _.eventsHistory[0].events)
        .flat()
        .filter((_) => _.ongoing)
        .map((_) => _?.historyItems?.[0].affectedComponents ?? [])
        .flat()
        .map((_) => _.componentStatus),
    [data?.Profile?.statusPage?.components]
  )

  const hasIssue =
    issues &&
    getHighestStatus(issues) !==
      StatusPageEventHistoryItemComponentStatus.Operational

  return (
    <section className={tw`h-full`}>
      <div data-testid="header" className={tw`h-20`}>
        <div
          style={{ background: profile?.background?.background ?? '#1E1E31' }}
          className={tw`flex items-center fixed top-0 h-20 w-full rounded-t-lg ${
            responsive && isMobile && 'rounded-none'
          }`}
        >
          <div
            onClick={() => navigate('/')}
            className={tw`flex items-center ml-4 mr-2 cursor-pointer`}
          >
            <ChevronLeftIcon className={tw`h-6 w-6 text-background-text`} />
          </div>
          <Avatars users={profile?.inbox?.users} />
          <div className={tw`h-full ml-4 py-5 text-background-text flex-grow`}>
            <p className={tw`text-sm`}>{profile?.account?.name}</p>
            {profile?.inbox?.users?.length && schedule.open ? (
              <p className={tw`text-sm opacity-80`}>
                {t('conversation.header.operators', {
                  count: profile?.inbox?.users?.length,
                })}
              </p>
            ) : null}
          </div>
          {responsive ? (
            <CloseButton
              className={tw`flex ml-2 mr-4 focus:outline-none ${
                !isMobile && 'hidden'
              }`}
              onClick={() => setOpen(false)}
            />
          ) : null}
        </div>
      </div>
      {hasIssue ? (
        <Link
          to="/status-page"
          className={tw`flex items-center justify-center bg-red-600 w-full text-sm text-white h-8`}
        >
          {t('status_page.issue_header')}
        </Link>
      ) : null}
      <div
        data-testid="chat"
        className={tw`${
          hasIssue ? 'h-[calc(100%-112px)]' : 'h-[calc(100%-80px)]'
        }`}
      >
        {children}
      </div>
    </section>
  )
}
